/**
 * 实时通信中枢核心类。
 *
 * 封装 `@comein/ws-lib` 的 `WsSharedClient`，对外提供 topic 订阅与请求/响应两种语义；
 * 内部按「连接 / 订阅 / 消息分发 / 请求响应 / 重连恢复 / 销毁」分块组织。
 *
 * 设计目标（与 plan 中的约束一致）：
 * 1. 业务侧不直接感知 WebSocket，统一通过 Hub 暴露的方法收发；
 * 2. 单实例足以承载全部业务通道（如确需多连接由调用方自行 new RealtimeHub）；
 * 3. 断连检测以 ws-lib 的 WebSocket 生命周期为主；Hub 层额外监听 `offline`/`online`，
 *    在浏览器判定断网时尽快降级状态，网络恢复后防抖重连（不修改 ws-lib 本身）。
 *
 * @module utils/realtime/RealtimeHub
 */

import { isFunction, isNil, isObject, isObjectLike, isString, isUndefined } from 'lodash-es';
import { WsSharedClient } from '@comein/ws-lib';
import slsLogger from '@/utils/slsLogger';
import {
  RealtimeStatus,
  RealtimeEvent,
  ProtocolField,
  DEFAULT_HUB_OPTIONS,
  DEFAULT_REQUEST_TIMEOUT_MS,
  DEFAULT_NETWORK_ONLINE_DEBOUNCE_MS
} from './constants';
import { getRealtimeWsToken } from './wsConnectOptions';

const REQUEST_ID_PREFIX = 'req';
/** 单调递增 requestId 计数器，避免随机碰撞和 Set 内存泄漏 */
let globalRequestIdCounter = 0;
/** 服务端主动断开最大允许次数，超过后不再重试连接 */
const MAX_SERVER_DISCONNECT_COUNT = 10;
/** 连接稳定时长阈值（毫秒）：open 后保持该时长以上再断开，才视为「正常抖动」清零重计 */
const SERVER_DISCONNECT_STABLE_MS = 30000;

/**
 * 解析订阅时传入的 subscribePayload，支持对象或函数两种形态。
 * @param {object | (() => object | null | undefined)} payload
 * @returns {object | null} 返回可被 send 的 payload；为空时返回 null
 */
function resolveSubscribePayload(payload) {
  // 函数形式允许业务在重连时动态拼接最新订阅参数（如可见区域内的股票代码）
  const value = isFunction(payload) ? payload() : payload;
  return value && isObject(value) ? value : null;
}

/**
 * 安全解析 JSON 字符串，失败时返回原值并打印警告。
 * @param {unknown} raw ws 原始消息
 * @returns {unknown} 解析后的值
 */
function safeParseMessage(raw) {
  if (!isString(raw)) return raw;
  try {
    return JSON.parse(raw);
  } catch (err) {
    console.warn('[RealtimeHub] message JSON parse failed, fallback to raw string', err);
    return raw;
  }
}

/**
 * 实时通信中枢。
 *
 * 典型用法：
 * @example
 * import { realtimeHub } from '@/utils/realtime';
 *
 * const off = realtimeHub.subscribe('stock_realtime', (data) => {
 *   // 处理推送
 * }, {
 *   subscribePayload: () => ({ type: 'stock_realtime', data: visibleCodes() })
 * });
 *
 * realtimeHub.send({ type: 'stock_realtime', data: codes });
 *
 * const res = await realtimeHub.request({ type: 'whoami' }, { timeout: 5000 });
 */
export class RealtimeHub {
  /**
   * @param {object} [options]
   * @param {string} [options.url] WebSocket 地址；缺省时不会自动发起连接
   * @param {'lazy' | 'eager'} [options.connect] 连接策略，默认 `lazy`
   * @param {number} [options.requestTimeoutMs] 默认 request 超时时间（毫秒）
   * @param {object | (() => object)} [options.wsClientOptions] 透传给 WsSharedClient 的额外选项；可为函数，在每次 connect 时解析以获取最新 token
   * @param {number} [options.networkOnlineDebounceMs] `online` 后延迟重连时间（毫秒），默认 1500
   */
  constructor(options = {}) {
    const merged = { ...DEFAULT_HUB_OPTIONS, ...options };

    /** @type {string | undefined} */
    this._url = merged.url;
    /** @type {'lazy' | 'eager'} */
    this._connectStrategy = merged.connect;
    /** @type {number} */
    this._requestTimeoutMs = merged.requestTimeoutMs || DEFAULT_REQUEST_TIMEOUT_MS;
    /** @type {object | undefined} */
    this._wsClientOptions = merged.wsClientOptions;
    /** @type {number} */
    this._networkOnlineDebounceMs = merged.networkOnlineDebounceMs ?? DEFAULT_NETWORK_ONLINE_DEBOUNCE_MS;

    // ----- 订阅表：topic -> Set<subscriber> -----
    // subscriber: { handler, subscribePayload }
    /** @type {Map<string, Set<{ handler: Function, subscribePayload?: object | Function }>>} */
    this._topicSubscribers = new Map();

    // ----- 事件总线：内置事件（status/reconnect/message/error）-----
    /** @type {Map<string, Set<Function>>} */
    this._eventListeners = new Map();

    // ----- 请求/响应 -----
    /** @type {Map<string | number, { resolve: Function, reject: Function, timer: ReturnType<typeof setTimeout> }>} */
    this._pendingRequests = new Map();
    /** 实例级 requestId 计数器，避免跨实例重复 */
    this._requestIdCounter = 0;

    /** @type {string} */
    this._status = RealtimeStatus.IDLE;
    /** @type {import('@comein/ws-lib').WsSharedClient | null} */
    this._wsClient = null;
    /** 标记是否已经尝试过 connect()，用于 lazy 模式 */
    this._everConnected = false;
    /** 用户主动 disconnect() 后为 true，`online` 时不自动重连 */
    this._userDisconnected = false;
    /** 浏览器处于 offline 态（含构造时 navigator.onLine === false） */
    this._networkOffline = typeof navigator !== 'undefined' ? !navigator.onLine : false;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this._onlineConfirmTimer = null;
    /** @type {boolean} destroy 后为 true，禁止再 connect / subscribe / send */
    this._destroyed = false;
    /** 当前 ws 客户端建立连接时使用的鉴权 token（用于检测重新登录后的 token 变更） */
    this._lastConnectToken = '';
    /** 服务端主动断开连接的连续次数（连接稳定保持后断开才清零重计），超过阈值后不再重试 */
    this._serverDisconnectCount = 0;
    /** 最近一次 open 的时间戳，用于区分「稳定连接后的正常断开」与「握手后立即被断开」 */
    this._lastOpenAt = 0;
    /** 服务端断开超过阈值后锁定为 true；由 disconnect() 解锁（含 RealtimeSubscriber.install() 的解锁重连路径） */
    this._serverDisconnectLocked = false;
    this._boundHandleOffline = () => this._handleOffline();
    this._boundHandleOnline = () => this._handleOnline();

    this._installNetworkListeners();

    if (this._connectStrategy === 'eager') {
      // eager 模式下立即触发连接；若 url 未提供，connect() 内部会跳过
      this.connect();
    }

    // 构造时若已离线，在 eager connect 之后尽快降级状态
    if (this._networkOffline) {
      queueMicrotask(() => this._handleOffline());
    }
  }

  // ============================================================
  //  连接管理
  // ============================================================

  /**
   * 解析 wsClientOptions，支持静态对象或每次 connect 时执行的工厂函数。
   * @returns {object}
   */
  _resolveWsClientOptions() {
    if (isFunction(this._wsClientOptions)) {
      const resolved = this._wsClientOptions();
      return resolved && isObject(resolved) ? resolved : {};
    }
    return this._wsClientOptions && isObject(this._wsClientOptions) ? this._wsClientOptions : {};
  }

  /**
   * 服务端断连次数超限被锁定时的错误对象。
   * @returns {Error}
   */
  _serverDisconnectLockedError() {
    return new Error(
      `RealtimeHub server disconnect count reached ${MAX_SERVER_DISCONNECT_COUNT}, reconnect locked. Call disconnect() first to reset.`
    );
  }

  /**
   * 主动建立连接。lazy 模式下，第一次 subscribe/send/request 会自动调用本方法。
   *
   * 如果未配置 `url`，将打印一次警告并跳过；如已有连接实例则复用，不会重复 connect。
   * @returns {void}
   */
  connect() {
    if (this._destroyed) {
      return;
    }
    // 服务端断开次数已达上限且未解锁，向业务层暴露失败状态后直接返回
    if (this._serverDisconnectLocked) {
      if (this._status !== RealtimeStatus.FAILED) {
        this._setStatus(RealtimeStatus.FAILED);
      }
      this._emit(RealtimeEvent.ERROR, this._serverDisconnectLockedError());
      return;
    }
    this._userDisconnected = false;
    this._serverDisconnectCount = 0;

    if (!this._url) {
      // 未配置 URL 时仅给出一次提示，避免污染日志
      if (!this._everConnected) {
        console.warn('[RealtimeHub] missing url, connect() skipped. 请配置 VITE_WS_URL 或在实例化时显式传入 url。');
        this._everConnected = true;
      }
      return;
    }
    if (this._wsClient) return;

    // 浏览器已离线时暂不建连，等 online 防抖后再 connect
    if (this._networkOffline) {
      if (this._status !== RealtimeStatus.RECONNECTING) {
        this._setStatus(RealtimeStatus.RECONNECTING);
      }
      return;
    }

    const wsClientOptions = this._resolveWsClientOptions();
    this._lastConnectToken = getRealtimeWsToken();
    this._wsClient = new WsSharedClient({
      url: this._url,
      // 手动 connect，便于先绑事件再发起连接，确保不漏掉 connecting/open
      autoConnect: false,
      ...wsClientOptions
    });

    this._bindWsEvents(this._wsClient);
    this._everConnected = true;
    this._wsClient.connect();
  }

  /**
   * 主动断开连接，清空所有 pendingRequests（以 rejection 形式通知调用方）。
   * 订阅关系保持不变，下一次 connect 后会重新发送 subscribePayload。
   * @returns {void}
   */
  disconnect() {
    this._userDisconnected = true;
    this._networkOffline = false;
    this._clearOnlineConfirmTimer();
    // 用户主动断开：解锁服务端断连限制、清空计数
    this._serverDisconnectLocked = false;
    this._serverDisconnectCount = 0;
    this._lastOpenAt = 0;
    this._teardownWsClient();
    this._rejectAllPending(new Error('RealtimeHub disconnected'));
    this._setStatus(RealtimeStatus.IDLE);
  }

  // ============================================================
  //  浏览器网络事件（offline / online）
  // ============================================================

  /**
   * 注册 `window` 的 offline / online 监听（仅浏览器环境）。
   * @returns {void}
   */
  _installNetworkListeners() {
    if (typeof window === 'undefined') {
      return;
    }
    window.addEventListener('offline', this._boundHandleOffline);
    window.addEventListener('online', this._boundHandleOnline);
  }

  /**
   * 移除网络事件监听。
   * @returns {void}
   */
  _uninstallNetworkListeners() {
    if (typeof window === 'undefined') {
      return;
    }
    window.removeEventListener('offline', this._boundHandleOffline);
    window.removeEventListener('online', this._boundHandleOnline);
  }

  /**
   * 清除 online 防抖计时器。
   * @returns {void}
   */
  _clearOnlineConfirmTimer() {
    if (this._onlineConfirmTimer) {
      clearTimeout(this._onlineConfirmTimer);
      this._onlineConfirmTimer = null;
    }
  }

  /**
   * 断开底层 ws 客户端并清空引用（不修改 Hub 的订阅表）。
   * @returns {void}
   */
  _teardownWsClient() {
    const client = this._wsClient;
    this._wsClient = null;
    if (client && isFunction(client.disconnect)) {
      try {
        client.disconnect();
      } catch (err) {
        console.warn('[RealtimeHub] ws disconnect failed', err);
      }
    }
  }

  /**
   * 本地鉴权 token 是否相对当前 ws 连接已更新（例如重新登录后）。
   * @returns {boolean}
   */
  _hasFreshConnectToken() {
    const latestToken = getRealtimeWsToken();
    return Boolean(latestToken && latestToken !== this._lastConnectToken);
  }

  /**
   * 丢弃仍绑定旧 token 的 ws 客户端，并使用最新 token 重建连接。
   * @returns {boolean} 是否已触发重连
   */
  _reconnectWithFreshToken() {
    if (this._destroyed || this._userDisconnected || !this._url) {
      return false;
    }
    if (!this._hasFreshConnectToken()) {
      return false;
    }
    this._teardownWsClient();
    this._setStatus(RealtimeStatus.RECONNECTING);
    queueMicrotask(() => {
      if (!this._destroyed && !this._userDisconnected) {
        this.connect();
      }
    });
    return true;
  }

  /**
   * 登录成功后若本地 token 已更新，则丢弃旧 ws 连接并用新 token 重建。
   * @returns {boolean} 是否已触发重连
   */
  reconnectWithFreshTokenIfNeeded() {
    return this._reconnectWithFreshToken();
  }

  /**
   * 浏览器 offline：尽快进入重连态并释放底层连接，避免 status 长时间停在 open。
   * @returns {void}
   */
  _handleOffline() {
    if (this._userDisconnected) {
      return;
    }
    if (this._networkOffline && !this._wsClient && this._status === RealtimeStatus.RECONNECTING) {
      return;
    }
    this._networkOffline = true;
    this._clearOnlineConfirmTimer();

    const wasActive =
      this._wsClient ||
      this._status === RealtimeStatus.OPEN ||
      this._status === RealtimeStatus.CONNECTING ||
      this._status === RealtimeStatus.CLOSED;

    if (!wasActive && this._status === RealtimeStatus.IDLE) {
      return;
    }

    // 断网导致实时连接被释放，上报 warn 便于区分「断网」与「服务端断开」两类中断原因
    slsLogger.console({
      component: 'RealtimeHub',
      level: 'warn',
      message: '实时通信-浏览器断网，连接释放待恢复',
      extra: { prevStatus: this._status, topics: this._getActiveTopics() }
    });
    this._rejectAllPending(new Error('RealtimeHub network offline'));
    this._teardownWsClient();
    this._setStatus(RealtimeStatus.RECONNECTING);
  }

  /**
   * 浏览器 online：防抖确认后按需重建连接。
   * @returns {void}
   */
  _handleOnline() {
    if (this._userDisconnected) {
      return;
    }
    this._clearOnlineConfirmTimer();
    this._onlineConfirmTimer = setTimeout(() => {
      this._onlineConfirmTimer = null;
      this._networkOffline = false;
      if (!this._shouldReconnectAfterNetworkRestore()) {
        return;
      }
      this.connect();
    }, this._networkOnlineDebounceMs);
  }

  /**
   * 网络恢复后是否应自动 connect。
   * @returns {boolean}
   */
  _shouldReconnectAfterNetworkRestore() {
    if (this._userDisconnected || !this._url || this._wsClient) {
      return false;
    }
    if (this._everConnected) {
      return true;
    }
    if (this._topicSubscribers.size > 0) {
      return true;
    }
    return this._connectStrategy === 'eager';
  }

  /** @returns {string} 当前连接状态（来自 RealtimeStatus） */
  get status() {
    return this._status;
  }

  /** @returns {boolean} 是否处于可发送状态 */
  get isOpen() {
    return this._status === RealtimeStatus.OPEN;
  }

  // ============================================================
  //  事件总线（status / reconnect / message / error）
  // ============================================================

  /**
   * 监听 Hub 内置事件（与按 topic 订阅业务消息相互独立）。
   * @param {string} event RealtimeEvent 中定义的事件名
   * @param {Function} handler 事件处理函数
   * @returns {() => void} 取消监听
   * @example
   * const off = realtimeHub.on('status', (s) => console.log(s));
   */
  on(event, handler) {
    if (!isFunction(handler)) return () => {};
    let set = this._eventListeners.get(event);
    if (!set) {
      set = new Set();
      this._eventListeners.set(event, set);
    }
    set.add(handler);

    // 订阅 status 时主动回调一次当前值，避免业务自己再去读
    if (event === RealtimeEvent.STATUS) {
      try {
        handler(this._status);
      } catch (err) {
        console.error('[RealtimeHub] status listener threw on initial emit', err);
      }
    }

    return () => set.delete(handler);
  }

  /**
   * 触发内置事件。
   * @param {string} event 事件名
   * @param {...unknown} args 事件参数
   * @returns {void}
   */
  _emit(event, ...args) {
    const set = this._eventListeners.get(event);
    if (!set || !set.size) return;
    for (const handler of set) {
      try {
        handler(...args);
      } catch (err) {
        console.error(`[RealtimeHub] listener of "${event}" threw`, err);
      }
    }
  }

  /**
   * 更新状态并广播。
   * @param {string} next 下一个状态
   */
  _setStatus(next) {
    if (this._status === next) return;
    this._status = next;
    this._emit(RealtimeEvent.STATUS, next);
  }

  // ============================================================
  //  订阅 / 取消订阅
  // ============================================================

  /**
   * 按 topic 订阅服务端推送。
   *
   * 多次订阅同一个 topic 不会互相覆盖，所有 handler 都会被依次调用。
   *
   * @param {string} topic 业务 topic，对应消息中的 `type` 字段
   * @param {(data: unknown, raw: object) => void} handler 收到消息时的回调
   * @param {object} [options]
   * @param {object | (() => object | null | undefined)} [options.subscribePayload]
   *   连接/重连成功后，hub 会自动把该 payload 发给后端，用于告知服务端订阅意图；
   *   传函数可在重连时动态拼接最新参数。
   * @returns {() => void} 取消订阅函数，幂等
   * @example
   * const off = realtimeHub.subscribe('stock_realtime', (data) => {
   *   // ...
   * }, { subscribePayload: () => ({ type: 'stock_realtime', data: getCodes() }) });
   */
  subscribe(topic, handler, options = {}) {
    if (this._destroyed) {
      return () => {};
    }
    if (!topic || !isFunction(handler)) {
      console.warn('[RealtimeHub] subscribe ignored: invalid topic or handler');
      return () => {};
    }

    let subscribers = this._topicSubscribers.get(topic);
    if (!subscribers) {
      subscribers = new Set();
      this._topicSubscribers.set(topic, subscribers);
    }

    const entry = {
      handler,
      subscribePayload: options.subscribePayload
    };
    subscribers.add(entry);

    // lazy 模式下，首次订阅触发连接
    if (this._connectStrategy === 'lazy' && !this._wsClient) {
      this.connect();
    }

    // 若已连接，立即发送一次订阅 payload，避免业务侧还要手动 send
    if (entry.subscribePayload && this.isOpen) {
      const payload = resolveSubscribePayload(entry.subscribePayload);
      if (payload) this._rawSend(payload);
    }

    let unsubscribed = false;
    return () => {
      if (unsubscribed) return;
      unsubscribed = true;
      const set = this._topicSubscribers.get(topic);
      if (!set) return;
      set.delete(entry);
      if (!set.size) this._topicSubscribers.delete(topic);
    };
  }

  // ============================================================
  //  发送 / 请求响应
  // ============================================================

  /**
   * 单向发送一条消息。lazy 模式下首次调用会触发连接。
   * 若当前连接尚未 OPEN，则直接丢弃并返回 false（避免引入离线缓冲队列）。
   * @param {object} payload 任意可序列化对象
   * @returns {boolean} 是否真正发送
   */
  send(payload) {
    if (this._destroyed) {
      return false;
    }
    // 锁定态下同步暴露失败状态与错误事件，不再尝试发送
    if (this._serverDisconnectLocked) {
      if (this._status !== RealtimeStatus.FAILED) {
        this._setStatus(RealtimeStatus.FAILED);
      }
      this._emit(RealtimeEvent.ERROR, this._serverDisconnectLockedError());
      return false;
    }
    if (this._connectStrategy === 'lazy' && !this._wsClient) {
      this.connect();
    }
    if (!this.isOpen) {
      // 业务侧若要保证可达，应在 status === 'open' 后再发，或使用 subscribePayload 让 hub 自动重发
      return false;
    }
    return this._rawSend(payload);
  }

  /**
   * 发送一条带 requestId 的请求并等待匹配的响应。
   *
   * 协议约定：后端在响应里回写相同的 `requestId`，hub 根据 requestId 关联 Promise。
   *
   * @param {object} payload 业务负载（hub 会自动附加 requestId）
   * @param {object} [options]
   * @param {number} [options.timeout] 超时时间（毫秒），默认 `requestTimeoutMs`
   * @returns {Promise<unknown>} 解析为后端响应中的 `data` 字段（若有）或整条消息
   * @example
   * const detail = await realtimeHub.request({ type: 'stock_detail', data: { code: 'SH.600519' } });
   */
  request(payload, options = {}) {
    return new Promise((resolve, reject) => {
      if (this._destroyed) {
        reject(new Error('RealtimeHub destroyed'));
        return;
      }
      // 锁定态下直接以明确原因拒绝，并同步暴露失败状态（connect() 内也会 emit）
      if (this._serverDisconnectLocked) {
        if (this._status !== RealtimeStatus.FAILED) {
          this._setStatus(RealtimeStatus.FAILED);
        }
        this._emit(RealtimeEvent.ERROR, this._serverDisconnectLockedError());
        reject(this._serverDisconnectLockedError());
        return;
      }
      if (this._connectStrategy === 'lazy' && !this._wsClient) {
        this.connect();
      }
      const timeoutMs = options.timeout ?? this._requestTimeoutMs;
      const requestId = this._nextRequestId();

      // 统一封装请求体，调用方无需关心 requestId 字段
      const enriched = { ...payload, [ProtocolField.REQUEST_ID]: requestId };

      const timer = setTimeout(() => {
        // 超时直接清理并拒绝
        if (this._pendingRequests.has(requestId)) {
          this._pendingRequests.delete(requestId);
          reject(new Error(`RealtimeHub request timeout after ${timeoutMs}ms`));
        }
      }, timeoutMs);

      this._pendingRequests.set(requestId, { resolve, reject, timer });

      // 未 OPEN 时不缓冲；调用方应等待连接就绪后再发，否则立即失败更可预测
      if (!this.isOpen) {
        clearTimeout(timer);
        this._pendingRequests.delete(requestId);
        reject(new Error('RealtimeHub not connected'));
        return;
      }
      this._rawSend(enriched);
    });
  }

  /**
   * 生成单调递增且不重复的 requestId（使用全局 + 实例级双重计数）。
   * 避免随机碰撞和 Set 内存泄漏问题。
   * @returns {string}
   */
  _nextRequestId() {
    const globalId = ++globalRequestIdCounter;
    const localId = ++this._requestIdCounter;
    return `${REQUEST_ID_PREFIX}-${globalId}-${localId}`;
  }

  /**
   * 真正调用 wsClient.send，并序列化为 JSON。
   * @param {object} payload
   * @returns {boolean} 是否发送
   */
  _rawSend(payload) {
    if (!this._wsClient) return false;
    if (isNil(payload)) {
      // 防御性拦截：避免把 "undefined" / "null" 字符串发上行链路
      console.warn('[RealtimeHub] send ignored: empty payload');
      return false;
    }
    try {
      this._wsClient.send(JSON.stringify(payload));
      return true;
    } catch (err) {
      console.error('[RealtimeHub] send failed', err);
      this._emit(RealtimeEvent.ERROR, err);
      return false;
    }
  }

  // ============================================================
  //  WS 事件绑定与消息分发
  // ============================================================

  /**
   * 判断事件是否来自当前持有的 ws 客户端（避免 teardown 后陈旧回调改乱状态）。
   * @param {import('@comein/ws-lib').WsSharedClient} client
   * @returns {boolean}
   */
  _isActiveWsClient(client) {
    return this._wsClient === client;
  }

  /**
   * 获取当前正在订阅的业务 topic 列表（如 stock_list_push / stock_kline），
   * 用于断连上报时定位受影响的业务通道。
   * @returns {string[]}
   */
  _getActiveTopics() {
    return [...this._topicSubscribers.keys()];
  }

  /**
   * 绑定 WsSharedClient 的连接生命周期事件，并把状态映射到内部 status。
   * @param {import('@comein/ws-lib').WsSharedClient} client
   */
  _bindWsEvents(client) {
    client.on('connecting', () => {
      if (!this._isActiveWsClient(client)) return;
      this._setStatus(RealtimeStatus.CONNECTING);
    });

    client.on('open', () => {
      if (!this._isActiveWsClient(client)) return;
      this._setStatus(RealtimeStatus.OPEN);
      // 记录本次连接建立时间：close 时据此判断连接是否稳定保持过，决定断连计数是否清零
      this._lastOpenAt = Date.now();
      // 连接就绪后下发所有 subscribePayload，恢复业务订阅
      this._resendAllSubscribePayloads();
    });

    client.on('message', (data) => {
      if (!this._isActiveWsClient(client)) return;
      this._dispatchMessage(safeParseMessage(data));
    });

    client.on('close', (code, reason) => {
      if (!this._isActiveWsClient(client)) return;
      // Hub 已因 offline 进入 reconnecting 时，避免被 close 覆盖为 closed
      if (this._networkOffline) {
        return;
      }
      // 上报 WS 断连日志，便于排查实时推送中断问题（offline 场景已在 _handleOffline 单独上报）
      slsLogger.console({
        component: 'RealtimeHub',
        level: this._userDisconnected ? 'info' : 'warn',
        message: '实时通信-WS连接断开',
        extra: {
          code,
          reason: reason || '',
          serverDisconnectCount: this._serverDisconnectCount,
          userDisconnected: this._userDisconnected,
          topics: this._getActiveTopics()
        }
      });
      // 断连时清空 pendingRequests，避免业务卡住
      this._rejectAllPending(new Error(`ws closed code=${code} reason=${reason || ''}`));
      // 重新登录后 token 已更新：终止 ws-lib 基于旧 token 的重连，改用新 token 建连
      if (this._reconnectWithFreshToken()) {
        return;
      }
      // 非用户主动断开、非离线断网时，判定为服务端主动断开，累加计数
      if (!this._userDisconnected) {
        // 连接保持超过稳定时长才视为「正常抖动」，清零后本次记为新周期第 1 次；
        // 握手后立即被断开（鉴权失败、1008/1006 等）则连续累加，保留熔断保护，
        // 避免 open（清零）→ close → 重连 → open 的无限重连风暴
        const heldMs = this._lastOpenAt ? Date.now() - this._lastOpenAt : 0;
        // 本次保持时长已被消费，清除时间戳，避免后续无 open 的 close 重复按「稳定连接」清零
        this._lastOpenAt = 0;
        this._serverDisconnectCount = heldMs >= SERVER_DISCONNECT_STABLE_MS ? 1 : this._serverDisconnectCount + 1;
        if (this._serverDisconnectCount >= MAX_SERVER_DISCONNECT_COUNT) {
          // 达到最大次数，锁定重连、释放客户端、置为 FAILED
          this._serverDisconnectLocked = true;
          this._teardownWsClient();
          this._setStatus(RealtimeStatus.FAILED);
          // 服务端断连次数超限属于异常场景，上报 error 便于告警排查
          slsLogger.console({
            component: 'RealtimeHub',
            level: 'error',
            message: '实时通信-服务端断连次数超限，已锁定重连',
            extra: {
              serverDisconnectCount: this._serverDisconnectCount,
              code,
              reason: reason || '',
              topics: this._getActiveTopics()
            }
          });
          return;
        }
      }
      // 进入 closed 由 ws-lib 决定是否重连；reconnecting 事件会再次更新状态
      this._setStatus(RealtimeStatus.CLOSED);
    });

    client.on('error', (err) => {
      if (!this._isActiveWsClient(client)) return;
      this._emit(RealtimeEvent.ERROR, err);
    });

    client.on('reconnecting', () => {
      if (!this._isActiveWsClient(client)) return;
      if (this._reconnectWithFreshToken()) {
        return;
      }
      // 服务端主动断开次数超过阈值，锁定并终止重连
      if (this._serverDisconnectCount >= MAX_SERVER_DISCONNECT_COUNT) {
        this._serverDisconnectLocked = true;
        this._teardownWsClient();
        this._setStatus(RealtimeStatus.FAILED);
        // 服务端断连次数超限属于异常场景，上报 error 便于告警排查
        slsLogger.console({
          component: 'RealtimeHub',
          level: 'error',
          message: '实时通信-服务端断连次数超限，已锁定重连',
          extra: { serverDisconnectCount: this._serverDisconnectCount, topics: this._getActiveTopics() }
        });
        return;
      }
      this._setStatus(RealtimeStatus.RECONNECTING);
    });

    client.on('reconnect', (attempt) => {
      if (!this._isActiveWsClient(client)) return;
      // ws-lib 在 reconnect 后会再次触发 open，订阅恢复在 open 钩子中处理
      this._emit(RealtimeEvent.RECONNECT, attempt);
    });

    client.on('reconnect_failed', () => {
      if (!this._isActiveWsClient(client)) return;
      // 重连最终失败，实时推送已不可用，上报 error 便于告警排查
      slsLogger.console({
        component: 'RealtimeHub',
        level: 'error',
        message: '实时通信-WS重连失败',
        extra: { serverDisconnectCount: this._serverDisconnectCount, topics: this._getActiveTopics() }
      });
      this._rejectAllPending(new Error('RealtimeHub reconnect failed'));
      // 释放失效 client，避免 connect() 因 _wsClient 仍存在而无法重建连接
      this._teardownWsClient();
      if (this._reconnectWithFreshToken()) {
        return;
      }
      this._setStatus(RealtimeStatus.FAILED);
    });
  }

  /**
   * 把消息按「请求响应 > topic 订阅 > 通用 message 事件」的优先级分发。
   * @param {unknown} payload 已解析的消息
   */
  _dispatchMessage(payload) {
    if (!payload || !isObjectLike(payload)) {
      // 非结构化消息，仅发到通用事件
      this._emit(RealtimeEvent.MESSAGE, payload);
      return;
    }

    const requestId = payload[ProtocolField.REQUEST_ID];
    // 请求响应优先：命中 pendingRequests 即认为是某次 request 的回包
    if (!isUndefined(requestId) && this._pendingRequests.has(requestId)) {
      const pending = this._pendingRequests.get(requestId);
      this._pendingRequests.delete(requestId);
      clearTimeout(pending.timer);
      // 约定上 resolve 业务数据，便于调用方少写一层 .data
      pending.resolve(payload[ProtocolField.DATA] ?? payload);
      return;
    }

    const topic = payload[ProtocolField.TYPE] ?? payload[ProtocolField.BUSINESS_TYPE];
    const subscribers = topic ? this._topicSubscribers.get(topic) : null;
    if (subscribers && subscribers.size) {
      // 复制一份避免遍历时被 unsubscribe 改动
      const snapshot = [...subscribers];
      for (const { handler } of snapshot) {
        try {
          handler(payload[ProtocolField.DATA], payload);
        } catch (err) {
          console.error(`[RealtimeHub] subscriber of "${topic}" threw`, err);
        }
      }
    }

    // 无论是否命中 topic，都对外暴露原始消息，便于上层做埋点/调试
    this._emit(RealtimeEvent.MESSAGE, payload);
  }

  // ============================================================
  //  重连恢复 / 资源清理
  // ============================================================

  /**
   * 把所有提供 subscribePayload 的订阅者重新通知给后端。
   * 在 `open`（包括首次和重连后）时调用。
   */
  _resendAllSubscribePayloads() {
    for (const subscribers of this._topicSubscribers.values()) {
      for (const { subscribePayload } of subscribers) {
        if (!subscribePayload) continue;
        const payload = resolveSubscribePayload(subscribePayload);
        if (payload) this._rawSend(payload);
      }
    }
  }

  /**
   * 以同一错误拒绝所有正在等待的请求并清空表。
   * @param {Error} error 拒绝原因
   */
  _rejectAllPending(error) {
    if (!this._pendingRequests.size) return;
    for (const { reject, timer } of this._pendingRequests.values()) {
      clearTimeout(timer);
      try {
        reject(error);
      } catch (err) {
        console.error('[RealtimeHub] reject pending failed', err);
      }
    }
    this._pendingRequests.clear();
  }

  /**
   * 销毁实例：断开连接、清理订阅与监听。本方法用于测试或热替换场景，
   * 业务运行期一般不需要主动调用。
   * @returns {void}
   */
  destroy() {
    if (this._destroyed) {
      return;
    }
    this._destroyed = true;
    this._uninstallNetworkListeners();
    this._clearOnlineConfirmTimer();
    this.disconnect();
    this._topicSubscribers.clear();
    this._eventListeners.clear();
    this._setStatus(RealtimeStatus.FAILED);
  }
}
